/**
 * Client-side lookup for the per-item volume discount.
 *
 * The table itself lives in snippets/volume-discount-matrix.liquid and is handed
 * to this file by layout/theme.liquid as window.BHS_VOLUME_MATRIX /
 * window.BHS_VOLUME_RATES, so the Liquid and the JS can never drift apart.
 *
 * The discount is PER LINE ITEM: the rate depends on that line's unit price
 * (which band) and that line's quantity (which rung). Nothing is pooled across
 * the cart. This replaced the old cart-subtotal tiers.
 *
 * Loaded blocking in <head> — it is tiny, has no dependencies, and several
 * inline scripts (product form, mini-cart popup) expect it to already exist.
 */
window.BHSVolumeDiscount = (function () {
  var bands = null;
  var rates = null;
  var quoteMultiplier = 0;

  function parse() {
    if (bands) return;

    rates = String(window.BHS_VOLUME_RATES || '')
      .split(',')
      .map(function (r) { return parseInt(r, 10) || 0; });

    quoteMultiplier = parseFloat(window.BHS_VOLUME_QUOTE_MULTIPLIER) || 0;

    bands = String(window.BHS_VOLUME_MATRIX || '')
      .split('|')
      .filter(Boolean)
      .map(function (band) {
        var parts = band.split(':');
        return {
          min: parseInt(parts[0], 10) || 0,
          // 0 means "no upper bound"
          max: parseInt(parts[1], 10) || 0,
          thresholds: String(parts[2] || '').split(',').map(function (t) {
            return parseInt(t, 10) || 0;
          })
        };
      });
  }

  function toCents(value) {
    var cents = parseInt(value, 10);
    return isNaN(cents) || cents < 0 ? 0 : cents;
  }

  /** The band a unit price falls in; lower-inclusive, upper-exclusive. */
  function bandFor(unitCents) {
    parse();
    var cents = toCents(unitCents);
    if (!cents) return null;

    for (var i = 0; i < bands.length; i++) {
      var band = bands[i];
      if (cents >= band.min && (band.max === 0 || cents < band.max)) {
        return band;
      }
    }
    return null;
  }

  /**
   * The rungs available at this unit price, ascending by quantity:
   * [{ quantity: 2, percent: 2 }, { quantity: 5, percent: 5 }, ...]
   * Rungs with a quantity of 0 are never reachable and are left out.
   */
  function tiersFor(unitCents) {
    var band = bandFor(unitCents);
    if (!band) return [];

    return band.thresholds
      .map(function (quantity, index) {
        return { quantity: quantity, percent: rates[index] || 0 };
      })
      .filter(function (tier) { return tier.quantity > 0 && tier.percent > 0; })
      .sort(function (a, b) { return a.quantity - b.quantity; });
  }

  /** Whole-number percent this line earns; the best rung it qualifies for wins. */
  function percentFor(unitCents, quantity) {
    var qty = parseInt(quantity, 10);
    if (isNaN(qty) || qty < 1) return 0;

    return tiersFor(unitCents).reduce(function (best, tier) {
      return qty >= tier.quantity && tier.percent > best ? tier.percent : best;
    }, 0);
  }

  /** The same as a decimal, e.g. 0.05. */
  function rateFor(unitCents, quantity) {
    return percentFor(unitCents, quantity) / 100;
  }

  /** Discounted unit price in cents — ceil, matching the Liquid and the cart. */
  function unitPriceFor(unitCents, quantity) {
    var cents = toCents(unitCents);
    return Math.ceil(cents * (1 - rateFor(cents, quantity)));
  }

  /**
   * The quantity at which the buyer should ask for a quote rather than take
   * the automatic break: the band's top rung times the quote multiplier.
   * 0 when the price falls in no band.
   */
  function quoteQuantityFor(unitCents) {
    var tiers = tiersFor(unitCents);
    if (!tiers.length || !quoteMultiplier) return 0;
    return Math.ceil(tiers[tiers.length - 1].quantity * quoteMultiplier);
  }

  return {
    bandFor: bandFor,
    tiersFor: tiersFor,
    percentFor: percentFor,
    rateFor: rateFor,
    unitPriceFor: unitPriceFor,
    quoteQuantityFor: quoteQuantityFor
  };
})();
